import React, { useMemo, useState } from 'react';
import {
  Box, Button, FormControl, FormLabel, HStack, IconButton, Menu, MenuButton, MenuDivider,
  MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Portal, Select, Text, Tooltip, useDisclosure, useToast,
} from '@chakra-ui/react';
import {
  FiCopy, FiDownload, FiEdit2, FiEye, FiMoreVertical,
} from 'react-icons/fi';
import { FaMoneyBill } from 'react-icons/fa';
import { NIL, v4 } from 'uuid';
import { QueryStatus } from 'react-query';
import { useHistory } from 'react-router-dom';
import IconBill from '../assets/Icon-Bills.svg';
import { Bill, BillStatus } from '../models/bill';
import useBill from '../hooks/useBill';
import useProjects from '../hooks/useProjects';
import useCurrentUser from '../hooks/useCurrentUser';
import DocumentGridShell from './DocumentGridShell';
import { useConfirm } from './ConfirmProvider'; // item 1500 — row-level confirm
import LedgerTable, { LedgerColumn, LedgerBulkAction } from './LedgerTable';
import LedgerSubline from './LedgerSubline';
import ProjectOptionsByCustomer from './ProjectOptionsByCustomer'; // item 1454
import { LEDGER_W } from './ledgerGrid';
import { isArchivedRow } from '../utils/archived'; // item 714
import StatusPill from './StatusPill';
import openPdf, { previewPdf } from '../utils/openPdf';
import { money } from '../utils/money';
import { canArchiveBill } from '../utils/archiveRules';
import { SqlDateTimeToDisplay } from '../utils/SqlTools'; // item 1331 — CREATED column

// item 555 — Bills on the shared LEDGER (last of the sweep; mirrors Purchase Orders, item 553).
// A Bill is the vendor's invoice to YOU (the expense/AP side), so there's no "send". Three-stage
// accountant lifecycle: New (created) → Entered (accountant enters into QB) → Paid (accountant
// pays). Mark entered / Mark paid are accountant/admin actions; anyone can view/download,
// duplicate, copy to another project. Void stays view-only.
const billStatusLabel = (value: BillStatus): string => {
  if (value === BillStatus.Paid) return 'Paid';
  if (value === BillStatus.Entered) return 'Entered';
  return 'New';
};
const amountOf = (bill: Bill): number => (bill as any).total ?? bill.costTotal ?? 0;

interface BillGridProps {
  data?: Bill[];
  status: QueryStatus;
  projectId?: string;
  collapse?: { collapsed: boolean; onToggle: () => void };
}

const BillGrid: React.FC<BillGridProps> = (props) => {
  const {
    data, status, projectId, collapse,
  } = props;
  const inProject = !!projectId && projectId !== NIL;
  const {
    tryArchive, tryVoid, voidMany, tryStatus, tryUpdate: tryAddBill, fetchById,
  } = useBill({ id: NIL });
  // item 142 — void/archive = Pulse Group only; item 555 — Entered/Paid = accountant/admin
  const { canModerate, isAdmin } = useCurrentUser();
  const { data: projects } = useProjects();
  const history = useHistory();
  const toast = useToast();
  // item 1500 (Chad) — the row ⋮ Void/Archive fired with NO confirm (the bulk bar asks; one
  // row didn't). Same shared dialog + wording family as the bulk bar. A bill has no un-void.
  const confirm = useConfirm();
  // EPIC_VOID_FLOW_2026-10 — the shared void dialog asks (reason + Notify customer); no pre-confirm
  const confirmVoid = (b: Bill) => { tryVoid(b.id); };
  const confirmArchive = async (b: Bill) => {
    if (!(await confirm(
      `Archive ${b.billNumber || 'this bill'}? It leaves the ledger (the Archived filter still shows it).`,
      { title: 'Archive bill', confirmLabel: 'Archive', destructive: true },
    ))) return;
    tryArchive(b.id);
  };

  const copyModal = useDisclosure();
  const [copyId, setCopyId] = useState('');
  const [copyProjectId, setCopyProjectId] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'entered' | 'paid'>('all');

  const onPdfError = () => toast({ status: 'error', title: 'Could not open the PDF' });
  const previewBill = (id: string) => previewPdf(`bills/${id}/pdf`).catch(onPdfError);
  const downloadBill = (id: string) => openPdf(`bills/${id}/pdf`).catch(onPdfError);
  const tryEdit = (id: string) => history.push(`/bill/${id}`);
  const openCopyModal = (id: string) => { setCopyId(id); setCopyProjectId(''); copyModal.onOpen(); };

  const duplicateBill = async (id: string) => {
    try {
      const original = await fetchById(id);
      if (!original) return;
      const copy = { ...original };
      copy.id = NIL;
      copy.billNumber = ''; // fresh auto-number (item 101)
      copy.billItems.forEach((i) => { i.id = v4(); });
      copy.isVoid = false;
      copy.isArchive = false;
      copy.isLocked = false;
      await tryAddBill(copy);
    } catch {
      toast({ status: 'error', title: 'Action failed', description: 'The document was NOT saved.' });
    }
  };

  const copyToProject = async () => {
    if (!copyProjectId) return;
    try {
      const original = await fetchById(copyId);
      if (!original) return;
      const copy = { ...original };
      copy.id = NIL;
      copy.projectId = copyProjectId;
      copy.billNumber = '';
      copy.billItems.forEach((i) => { i.id = v4(); });
      copy.isVoid = false;
      copy.isArchive = false;
      copy.isLocked = false;
      await tryAddBill(copy);
      toast({ status: 'success', title: 'Copied to project' });
    } catch {
      toast({ status: 'error', title: 'Action failed', description: 'The document was NOT saved.' });
    }
    copyModal.onClose();
  };

  const columns: LedgerColumn<Bill>[] = useMemo(() => [
    {
      // item 1445 (Chad, after the 1444 invoice fold: "I thought we were doing a pass on all the ledgers") —
      // BILL = the portal pattern: number bold, vendor (or service name) as two-line subtext, then
      // the project outside a project. NUMBER + NAME folded into one flexible column (300px floor).
      // item 1447 — vendor / service name + project ride the full-width rowSubline; number here.
      key: 'number',
      header: 'BILL',
      width: LEDGER_W.id, // item 1449 — shared column positions (see ledgerGrid.ts)
      sortValue: (b) => b.billNumber ?? '',
      render: (b) => <Text fontWeight="semibold" whiteSpace="nowrap">{b.billNumber || '—'}</Text>,
    },
    {
      // item 1331 (Chad: "do we have room to add a date") — CREATED date; the ledger's default sort
      // (newest on top) so the chevron has a column to sit on. Number order lies for revisions.
      key: 'created',
      header: 'CREATED',
      width: LEDGER_W.created,
      sortValue: (b) => (b as { createdDate?: string; lastModifiedDate?: string }).createdDate ?? (b as { lastModifiedDate?: string }).lastModifiedDate ?? '',
      render: (b) => {
        const d = (b as { createdDate?: string; lastModifiedDate?: string }).createdDate ?? (b as { lastModifiedDate?: string }).lastModifiedDate;
        return <span style={{ whiteSpace: 'nowrap' }}>{d ? SqlDateTimeToDisplay(d) : '—'}</span>;
      },
    },
    // NAME (vendor + project subtitle) folded under the number — item 1445.
    {
      key: 'amount', header: 'AMOUNT', width: LEDGER_W.slot34, align: 'right', sortValue: (b) => amountOf(b), render: (b) => money(amountOf(b)),
    },
    {
      key: 'status',
      header: 'STATUS',
      width: LEDGER_W.status,
      sortValue: (b) => b.status,
      render: (b) => <StatusPill label={billStatusLabel(b.status)} isVoid={b.isVoid} minW="96px" />,
    },
  ], []);

  const iconSpacer = <Box w="24px" flexShrink={0} />;
  const previewBtn = (b: Bill) => (
    <Tooltip label="Preview" openDelay={300}>
      <IconButton size="xs" variant="ghost" aria-label="Preview" icon={<FiEye />} onClick={() => previewBill(b.id)} />
    </Tooltip>
  );
  const downloadBtn = (b: Bill) => (
    <Tooltip label="Download PDF" openDelay={300}>
      <IconButton size="xs" variant="ghost" aria-label="Download PDF" icon={<FiDownload />} onClick={() => downloadBill(b.id)} />
    </Tooltip>
  );

  const rowActions = (b: Bill): React.ReactNode => {
    // a VOID bill stays viewable (view/download only)
    if (b.isVoid) {
      return (
        <HStack spacing={1} justify="flex-end">
          {iconSpacer}
          {previewBtn(b)}
          {downloadBtn(b)}
          {iconSpacer}
        </HStack>
      );
    }
    // item 555 — forward lifecycle: New → Mark entered → Mark paid. Accountant/admin only; other
    // roles just see the pill + view/download/duplicate. A Paid bill has no forward primary.
    const primary = () => {
      if (!isAdmin) return iconSpacer;
      if (b.status === BillStatus.New) return <Button size="xs" minW="96px" colorScheme="brandGreen" onClick={() => tryStatus(b.id, BillStatus.Entered)}>Mark entered</Button>;
      if (b.status === BillStatus.Entered) return <Button size="xs" minW="96px" colorScheme="brandGreen" leftIcon={<FaMoneyBill />} onClick={() => tryStatus(b.id, BillStatus.Paid)}>Mark paid</Button>;
      return iconSpacer;
    };
    return (
      <HStack spacing={1} justify="flex-end">
        {primary()}
        <Tooltip label="Duplicate" openDelay={300}>
          <IconButton size="xs" variant="ghost" aria-label="Duplicate" icon={<FiCopy />} onClick={() => duplicateBill(b.id)} />
        </Tooltip>
        {previewBtn(b)}
        {downloadBtn(b)}
        <Menu isLazy>
          <MenuButton as={IconButton} size="xs" variant="ghost" aria-label="More actions" icon={<FiMoreVertical />} />
          <Portal>
            <MenuList fontSize="sm">
              <MenuItem icon={<FiEdit2 />} onClick={() => tryEdit(b.id)}>Edit</MenuItem>
              <MenuItem onClick={() => openCopyModal(b.id)}>Copy to project…</MenuItem>
              {/* item 555 — accountant corrections: step the lifecycle back */}
              {isAdmin && b.status === BillStatus.Entered && <MenuItem onClick={() => tryStatus(b.id, BillStatus.New)}>Mark new (undo entered)</MenuItem>}
              {isAdmin && b.status === BillStatus.Paid && <MenuItem onClick={() => tryStatus(b.id, BillStatus.Entered)}>Mark entered (undo paid)</MenuItem>}
              {canModerate && <MenuDivider />}
              {/* item 1500 — confirm first, exactly like the bulk bar */}
              {canModerate && <MenuItem onClick={() => confirmVoid(b)}>Void</MenuItem>}
              {canModerate && canArchiveBill(b) && <MenuItem onClick={() => confirmArchive(b)}>Archive</MenuItem>}
            </MenuList>
          </Portal>
        </Menu>
      </HStack>
    );
  };

  const bulkActions: LedgerBulkAction<Bill>[] = canModerate ? [
    {
      label: 'Archive',
      appliesTo: (b) => !isArchivedRow(b) && canArchiveBill(b), // item 1570 — void first
      confirm: (rows) => `Archive ${rows.length} bill(s)?`,
      onRun: async (rows) => { await Promise.all(rows.map((b) => tryArchive(b.id))); },
    },
    {
      label: 'Void',
      colorScheme: 'red',
      appliesTo: (b) => !b.isVoid,
      onRun: async (rows) => { await voidMany(rows.map((b) => b.id)); }, // EPIC_VOID_FLOW — one dialog
    },
  ] : [];

  const rows = useMemo(() => {
    const list = (data ?? []).filter((b) => !isArchivedRow(b));
    if (statusFilter === 'new') return list.filter((b) => b.status === BillStatus.New && !b.isVoid);
    if (statusFilter === 'entered') return list.filter((b) => b.status === BillStatus.Entered && !b.isVoid);
    if (statusFilter === 'paid') return list.filter((b) => b.status === BillStatus.Paid && !b.isVoid);
    return list;
  }, [data, statusFilter]);

  return (
    <>
      <DocumentGridShell
        heading="BILLS"
        icon={IconBill}
        addPath={`/bill/${NIL}${inProject ? `?projectId=${projectId}` : ''}`}
        collapsible={!!collapse}
        collapsed={collapse?.collapsed}
        onToggleCollapse={collapse?.onToggle}
        status={status}
        // item 712 (Chad) — header count must match the visible (archived-filtered) rows
        data={data === undefined ? undefined : rows}
      >
        <LedgerTable<Bill>
          defaultSort={{ key: 'created', dir: -1 }} // item 1331 (Chad) — DATE order, newest on top (number order lies for revisions)
          rows={rows}
          columns={columns}
          rowSubline={(b) => <LedgerSubline name={b.vendor?.name ?? (b as any).vendorName ?? b.serviceName} meta={inProject ? [] : [(b as any).projectName]} />}
          rowId={(b) => b.id}
          rowActions={rowActions}
          rowActionsWidth={LEDGER_W.actions}
          rowActionsFlex
          rowActionsHeader="ACTION"
          bulkActions={bulkActions}
          selectable={canModerate}
          filters={(
            <Select size="sm" maxW="200px" variant="filled" borderRadius="lg" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
              <option value="all">ALL STATUSES</option>
              <option value="new">NEW</option>
              <option value="entered">ENTERED</option>
              <option value="paid">PAID</option>
            </Select>
          )}
          searchText={(b) => `${b.billNumber ?? ''} ${b.vendor?.name ?? (b as any).vendorName ?? ''} ${b.serviceName ?? ''}`}
          searchPlaceholder="Search bills…"
          onRowClick={(b) => tryEdit(b.id)}
          emptyText="No bills yet."
        />
      </DocumentGridShell>
      {/* copy-to-project (mirrors the Purchase Orders grid) */}
      <Modal isOpen={copyModal.isOpen} onClose={copyModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Copy bill to another project</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Destination project</FormLabel>
              <Select placeholder="Select a project" value={copyProjectId} onChange={(e) => setCopyProjectId(e.target.value)}>
                {/* item 1454 — grouped under the CUSTOMER */}
                <ProjectOptionsByCustomer projects={projects?.index} />
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={copyModal.onClose}>Cancel</Button>
            <Button colorScheme="brandGreen" isDisabled={!copyProjectId} onClick={copyToProject}>Copy</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

BillGrid.defaultProps = {
  data: undefined,
  projectId: NIL,
  collapse: undefined,
};

export default BillGrid;
